'use client';

import { useCallback, useEffect } from 'react';

import { useTranslations } from 'next-intl';

import { useCommentsContext } from '@/components/Contexts/CommentsContext';
import { useViewerRole } from '@/components/Contexts/ViewerRoleContext';
import { useWorkbenchConfig } from '@/components/Contexts/WorkbenchConfigContext';
import { showErrorToast } from '@/components/ui/ToastNotification';
import { useApiClient } from '@/hooks/useApiClient';

import type {
  CommentReply,
  Pin,
  ProxyDeleteReplyFailedMessage,
  ProxyDeleteReplyMessage,
  ProxyReplyDeletedMessage,
} from '@/types/feedback';

interface DeleteReplyResult {
  replies: CommentReply[];
  revision: string | null;
}

export function useDeleteReply() {
  const t = useTranslations();
  const { dispatch } = useCommentsContext();
  const { isPartner } = useViewerRole();
  // feedbackId, metaSiteId and isOwner travel inside the sealed context. The
  // partner path does not forward it: the proxy uses its own owner context.
  const { sealedContext } = useWorkbenchConfig();
  const apiClient = useApiClient();

  useEffect(() => {
    if (!isPartner) return;

    function handleDeleted(event: MessageEvent) {
      if (event.data?.type !== 'FB_REPLY_DELETED') return;
      const { pinId, replies, revision } = (
        event.data as ProxyReplyDeletedMessage
      ).payload;
      dispatch({
        type: 'UPDATE_PIN_REPLIES',
        payload: { pinId, replies, revision },
      });
    }

    function handleFailed(event: MessageEvent) {
      if (event.data?.type !== 'FB_DELETE_REPLY_FAILED') return;
      const { pinId, replyIndex } = (
        event.data as ProxyDeleteReplyFailedMessage
      ).payload;
      // The optimistic removal already happened; on failure we can't easily restore
      // the original reply content, so just surface the error. The user can refresh.
      void pinId;
      void replyIndex;
      showErrorToast(t('get-feedback-toast-deleteReplyFailed'));
    }

    window.addEventListener('message', handleDeleted);
    window.addEventListener('message', handleFailed);
    return () => {
      window.removeEventListener('message', handleDeleted);
      window.removeEventListener('message', handleFailed);
    };
  }, [isPartner, dispatch, t]);

  const deleteReply = useCallback(
    async (pin: Pin, replyIndex: number) => {
      if (!pin.commentId) return;

      dispatch({
        type: 'REMOVE_REPLY',
        payload: { pinId: pin.id, replyIndex },
      });

      if (isPartner) {
        const msg: ProxyDeleteReplyMessage = {
          type: 'FB_DELETE_REPLY',
          payload: {
            pinId: pin.id,
            commentId: pin.commentId,
            replyIndex,
            revision: pin.revision,
          },
        };
        window.postMessage(msg, window.location.origin);
        return;
      }

      try {
        const result = await apiClient
          .post<DeleteReplyResult>('api/delete-reply', {
            ctx: sealedContext,
            commentId: pin.commentId,
            replyIndex,
            revision: pin.revision,
          })
          .then((r) => r.data);

        dispatch({
          type: 'UPDATE_PIN_REPLIES',
          payload: {
            pinId: pin.id,
            replies: result.replies,
            revision: result.revision,
          },
        });
      } catch {
        showErrorToast(t('get-feedback-toast-deleteReplyFailed'));
      }
    },
    [apiClient, dispatch, isPartner, sealedContext, t],
  );

  return { deleteReply };
}
